import { type SelectedUserData } from '~/data/user/useQueryUserData';

import { useAppForm } from '~/ui/molecules/TanstackForm';

import { clearDraft, loadDraft, saveDraft } from './persistDraft';
import { submitBugReport } from './submitBugReport';
import { type BugReportFormValues, zodBugReportSchema } from './validations';

type UseBugReportFormParams = {
  readonly currentUser: SelectedUserData | undefined;
  readonly derivedName: string | null | undefined;
  readonly harFile: File | undefined;
  readonly screenshot: File | undefined;
  readonly onSubmitError: () => void;
  readonly onSubmitSuccess: () => void;
};

/**
 * Builds the form's default values from the persisted draft (if any) and the
 * reporter name derived from the current user. Treated as the single source
 * of truth for "what should the form contain when the dialog is shown".
 */
export function getDefaultFormValues({
  derivedName,
}: {
  readonly derivedName: string | null | undefined;
}): BugReportFormValues {
  const draft = loadDraft();

  return {
    actualBehavior: draft?.actualBehavior ?? '',
    expectedBehavior: draft?.expectedBehavior ?? '',
    reporterName: draft?.reporterName || derivedName || '',
    stepsToReproduce: draft?.stepsToReproduce ?? '',
    title: draft?.title ?? '',
    videoLink: draft?.videoLink ?? '',
  };
}

export function useBugReportForm({
  currentUser,
  derivedName,
  harFile,
  screenshot,
  onSubmitError,
  onSubmitSuccess,
}: UseBugReportFormParams) {
  return useAppForm({
    defaultValues: getDefaultFormValues({ derivedName }),
    // Autosave the in-progress form as a draft so it survives refresh,
    // minimize, or accidental close. The lenient draft schema lets us
    // persist partial / invalid values.
    listeners: {
      onChange({ formApi }) {
        saveDraft(formApi.state.values);
      },
      onChangeDebounceMs: 1000,
    },
    async onSubmit({ formApi, value }) {
      const validatedValues = zodBugReportSchema.parse(value);

      try {
        await submitBugReport({
          currentUser,
          harFile,
          screenshot,
          values: validatedValues,
        });

        clearDraft();
        formApi.reset();
        onSubmitSuccess();
      } catch {
        onSubmitError();
      }
    },
    validators: {
      onChange: zodBugReportSchema,
    },
  });
}

export type BugReportForm = ReturnType<typeof useBugReportForm>;
